<template>
  <v-card class="pa-6">
    <v-card-title class="text-uppercase d-flex align-center justify-space-between pa-0 mb-1">
      <span>Club de Descuentos</span>
      <v-btn icon small @click="$emit('close-dialog')">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-card-title>
    <p class="text-body-2 grey--text mb-4">
      Activa o desactiva alianzas para los usuarios de este plan. Las activas aparecen en la app.
    </p>
    <hr class="my-3" />

    <div v-if="loading" class="d-flex justify-center py-6">
      <v-progress-circular indeterminate color="primary" />
    </div>

    <template v-else>
      <!-- Sección alianzas exclusivas -->
      <template v-if="exclusiveAlliances.length">
        <p class="text-caption text-uppercase font-weight-bold purple--text mb-1">Alianzas exclusivas</p>
        <v-list dense>
          <template v-for="(alliance, i) in exclusiveAlliances">
            <v-list-item :key="'ex-' + alliance.id">
              <v-list-item-avatar size="36">
                <v-img v-if="alliance.imageCardUrl" :src="alliance.imageCardUrl" />
                <v-icon v-else color="purple">mdi-tag</v-icon>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title class="text-body-2">{{ alliance.name }}</v-list-item-title>
                <v-list-item-subtitle>
                  <v-chip x-small color="purple" dark class="mt-1">Exclusiva</v-chip>
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-switch
                  :input-value="!isExcluded(alliance.id)"
                  :loading="toggling[alliance.id]"
                  dense
                  hide-details
                  color="purple"
                  @change="toggle(alliance, $event)"
                />
              </v-list-item-action>
            </v-list-item>
            <v-divider v-if="i < exclusiveAlliances.length - 1" :key="'exd-' + i" />
          </template>
        </v-list>
        <hr class="my-3" />
      </template>

      <!-- Sección alianzas públicas -->
      <p class="text-caption text-uppercase font-weight-bold primary--text mb-1">Alianzas públicas</p>
      <p v-if="!publicAlliances.length" class="text-body-2 grey--text">
        No hay alianzas públicas disponibles.
      </p>

      <v-list dense v-else>
        <template v-for="(alliance, i) in publicAlliances">
          <v-list-item :key="alliance.id">
            <v-list-item-avatar size="36">
              <v-img v-if="alliance.imageCardUrl" :src="alliance.imageCardUrl" />
              <v-icon v-else>mdi-tag</v-icon>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title class="text-body-2">{{ alliance.name }}</v-list-item-title>
            </v-list-item-content>
            <v-list-item-action>
              <v-switch
                :input-value="!isExcluded(alliance.id)"
                :loading="toggling[alliance.id]"
                dense
                hide-details
                color="primary"
                @change="toggle(alliance, $event)"
              />
            </v-list-item-action>
          </v-list-item>
          <v-divider v-if="i < publicAlliances.length - 1" :key="'d-' + i" />
        </template>
      </v-list>

      <p v-if="error" class="caption red--text mt-3 mb-0">{{ error }}</p>
    </template>

    <hr class="my-3" />
    <v-card-actions class="justify-end pa-0">
      <v-btn color="primary" rounded depressed @click="$emit('close-dialog')">Cerrar</v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import AlliancesService from '../services/alliances.service';
import PlansService from '../services/plans.service';

export default {
  name: 'AlliancePlanMantainer',
  props: {
    plan: { type: Object, required: true }
  },
  data() {
    return {
      publicAlliances: [],
      exclusiveAlliances: [],
      excludedIds: new Set(),
      loading: false,
      toggling: {},
      error: ''
    };
  },
  mounted() {
    this.loadData();
  },
  methods: {
    async loadData() {
      this.loading = true;
      this.error = '';
      try {
        const [alliancesRes, exclusionsRes] = await Promise.all([
          AlliancesService.getAll(1, 1000, '', null),
          PlansService.getPlanAllianceExclusions(this.plan.id)
        ]);
        if (alliancesRes.status === 'ok') {
          const raw = alliancesRes.data.alliances ?? alliancesRes.data;
          const planCountryId = this.plan.countryId ?? null;
          this.publicAlliances = raw.filter(a =>
            a.type !== 'exclusive' &&
            (planCountryId === null || a.countryId === null || a.countryId === planCountryId)
          );
          const planCompanyIds = this.plan.companyIds ?? [];
          this.exclusiveAlliances = raw.filter(a =>
            a.type === 'exclusive' &&
            planCompanyIds.includes(a.exclusiveCompanyId)
          );
        }
        const exclusiveIds = new Set(this.exclusiveAlliances.map(a => a.id));
        const planExclusionIds = exclusionsRes.status === 'ok'
          ? exclusionsRes.data.filter(e => !exclusiveIds.has(e.allianceId)).map(e => e.allianceId)
          : [];
        const inactiveExclusiveIds = this.exclusiveAlliances
          .filter(a => !a.active)
          .map(a => a.id);
        this.excludedIds = new Set([...planExclusionIds, ...inactiveExclusiveIds]);
      } catch {
        this.error = 'Error al cargar los datos. Intenta nuevamente.';
      }
      this.loading = false;
    },

    isExcluded(allianceId) {
      return this.excludedIds.has(allianceId);
    },

    async toggle(alliance, included) {
      this.$set(this.toggling, alliance.id, true);
      this.error = '';
      try {
        let res;
        if (alliance.type === 'exclusive') {
          res = await AlliancesService.update(alliance.id, { active: included });
          if (res.status === 'ok') {
            try {
              if (included) {
                await PlansService.removePlanAllianceExclusion(this.plan.id, alliance.id);
              } else {
                await PlansService.addPlanAllianceExclusion(this.plan.id, alliance.id);
              }
            } catch {
              // sync best-effort: no bloquea el flujo principal si falla
            }
          }
        } else if (included) {
          res = await PlansService.removePlanAllianceExclusion(this.plan.id, alliance.id);
        } else {
          res = await PlansService.addPlanAllianceExclusion(this.plan.id, alliance.id);
        }

        if (res.status === 'ok') {
          if (included) {
            this.excludedIds.delete(alliance.id);
          } else {
            this.excludedIds.add(alliance.id);
          }
          this.excludedIds = new Set(this.excludedIds);
        } else {
          this.error = `Error al actualizar "${alliance.name}". Intenta nuevamente.`;
        }
      } catch {
        this.error = `Error al actualizar "${alliance.name}". Intenta nuevamente.`;
      }
      this.$set(this.toggling, alliance.id, false);
    }
  }
};
</script>
